import type { JSX } from 'react';

import classNames from 'classnames';
import type { Editor } from '@tiptap/react';

import { HoverCard } from '../HoverCard/HoverCard';

import styles from './RichTextEditor.module.css';

export interface ToolbarButtonProps {
  label: string;
  active?: boolean;
  /** For a command that does not apply where the caret is; the button is always off without an editor. */
  disabled?: boolean;
  editor: Editor | null;
  onClick: (editor: Editor) => void;
  children: JSX.Element;
}

/**
 * One icon button in an editor toolbar.
 *
 * Shared by the Markdown composer and the signature editor rather than
 * duplicated in each: the two toolbars sit side by side in the product, and a
 * copy in each file lets the pressed state, the disabled rule and the
 * `onMouseDown` guard that keeps the selection alive drift apart between them.
 *
 * `tabIndex={-1}` keeps the formatting controls out of sequential Tab order
 * so Tab from Subject lands in the editor instead of walking Bold → Italic → …
 * Mouse still reaches every control; `aria-pressed` keeps the toolbar operable
 * for assistive tech.
 */
export function ToolbarButton({
  label,
  active,
  disabled,
  editor,
  onClick,
  children,
}: ToolbarButtonProps): JSX.Element {
  return (
    <HoverCard content={label} side="bottom" delay={150} sideOffset={4}>
      <button
        type="button"
        tabIndex={-1}
        className={classNames(styles.ToolButton, { [styles.ToolOn]: active })}
        aria-label={label}
        aria-pressed={active ?? false}
        disabled={!editor || disabled}
        // Without this the button steals focus and collapses the selection
        // before the command runs, so formatting would apply to nothing.
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => editor && onClick(editor)}
      >
        {children}
      </button>
    </HoverCard>
  );
}

export default ToolbarButton;
